/**
 * Bulk Create Modal Component
 * Extracted from Schedule.jsx for maintainability
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ScrollArea } from '../ui/scroll-area';
import { TimeSelect } from './TimeSelect';
import { CalendarRange, Loader2 } from 'lucide-react';
import { fmt } from '../../lib/currency';
import { useAuth } from '../../context/AuthContext';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

export function BulkCreateModal({
  open,
  onOpenChange,
  bulkData,
  setBulkData,
  bulkPreview,
  setBulkPreview,
  bulkLoading,
  onPreview,
  onCreate,
  contracts = [],
  jobTypes = [],
}) {
  const { user } = useAuth();
  const orgCurrency = user?.organisation_currency || 'GBP';
  const toggleSpecificDay = (day) => {
    setBulkData(prev => ({
      ...prev,
      specific_days: prev.specific_days.includes(day)
        ? prev.specific_days.filter(d => d !== day)
        : [...prev.specific_days, day]
    }));
    setBulkPreview(null);
  };

  return (
    <Dialog open={open} onOpenChange={(openState) => { 
      onOpenChange(openState); 
      if (!openState) setBulkPreview(null); 
    }}>
      <DialogContent className="bg-[#2B2B2B] border-white/10 text-[#F2F2F2] max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-[#1ABC9C]" />
            Bulk Create Recurring Shifts
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            Create multiple shifts at once based on a recurrence pattern
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 py-4">
          {/* Recurrence Pattern */}
          <div className="space-y-2">
            <Label>Recurrence Pattern</Label>
            <Select 
              value={bulkData.recurrence_pattern} 
              onValueChange={(v) => { setBulkData(d => ({ ...d, recurrence_pattern: v })); setBulkPreview(null); }}
            >
              <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="recurrence-pattern-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#2B2B2B] border-white/10">
                <SelectItem value="daily">Daily (Every day)</SelectItem>
                <SelectItem value="weekly">Weekly (Same day each week)</SelectItem>
                <SelectItem value="weekdays">Weekdays (Mon-Fri)</SelectItem>
                <SelectItem value="specific_days">Specific Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Specific Days Selection */}
          {bulkData.recurrence_pattern === 'specific_days' && (
            <div className="space-y-2">
              <Label>Select Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAYS_OF_WEEK.map(day => (
                  <Button
                    key={day.value}
                    type="button"
                    variant={bulkData.specific_days.includes(day.value) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleSpecificDay(day.value)}
                    className={bulkData.specific_days.includes(day.value) 
                      ? "bg-[#1ABC9C] hover:bg-[#1ABC9C]/90" 
                      : "border-white/10 text-[#A1A1AA]"
                    }
                  >
                    {day.label}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Date Range */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={bulkData.start_date}
                onChange={(e) => { setBulkData(d => ({ ...d, start_date: e.target.value })); setBulkPreview(null); }}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={bulkData.end_date}
                onChange={(e) => { setBulkData(d => ({ ...d, end_date: e.target.value })); setBulkPreview(null); }}
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
          </div>

          {/* Time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Start Time</Label>
              <TimeSelect
                value={bulkData.start_time}
                onChange={(v) => { setBulkData(d => ({ ...d, start_time: v })); setBulkPreview(null); }}
                testIdPrefix="bulk-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label>End Time</Label>
              <TimeSelect
                value={bulkData.end_time}
                onChange={(v) => { setBulkData(d => ({ ...d, end_time: v })); setBulkPreview(null); }}
                testIdPrefix="bulk-end-time"
              />
            </div>
          </div>

          {/* Location & Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Site / Venue Name *</Label>
              <Input
                value={bulkData.location}
                onChange={(e) => { setBulkData(d => ({ ...d, location: e.target.value })); setBulkPreview(null); }}
                placeholder="e.g. St Thomas Hospital"
                className="bg-[#1A1A1A] border-white/10"
              />
            </div>
            <div className="space-y-2">
              <Label>Shift Type</Label>
              <Select 
                value={bulkData.shift_type} 
                onValueChange={(v) => { setBulkData(d => ({ ...d, shift_type: v })); setBulkPreview(null); }}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="night">Night</SelectItem>
                  <SelectItem value="standby">Standby</SelectItem>
                  <SelectItem value="emergency">Emergency</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Address + postcode (iter64) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Address</Label>
              <Input
                value={bulkData.address || ''}
                onChange={(e) => { setBulkData(d => ({ ...d, address: e.target.value })); setBulkPreview(null); }}
                placeholder="Street + town"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="bulk-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Postcode <span className="text-red-400">*</span></Label>
              <Input
                value={bulkData.postcode || ''}
                onChange={(e) => {
                  setBulkData(d => ({ ...d, postcode: e.target.value.toUpperCase() }));
                  setBulkPreview(null);
                }}
                placeholder="e.g. SE1 7EH"
                className="bg-[#1A1A1A] border-white/10"
                data-testid="bulk-postcode-input"
              />
            </div>
          </div>

          {/* Contract + Job Type (iter63 — required chain) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                Contract <span className="text-red-400">*</span>
              </Label>
              <Select
                value={bulkData.contract_id || ""}
                onValueChange={(v) => {
                  setBulkData(d => ({ ...d, contract_id: v, job_type_id: "" }));
                  setBulkPreview(null);
                }}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="bulk-contract-select">
                  <SelectValue placeholder="Select contract" />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {contracts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>
                Job Type <span className="text-red-400">*</span>
              </Label>
              <Select
                value={bulkData.job_type_id || ""}
                onValueChange={(v) => {
                  const jt = jobTypes.find(j => j.id === v);
                  setBulkData(d => ({
                    ...d,
                    job_type_id: v,
                    rate_payable: jt?.default_rate_payable ?? d.rate_payable,
                    rate_chargeable: jt?.default_rate_chargeable ?? d.rate_chargeable,
                  }));
                  setBulkPreview(null);
                }}
                disabled={!bulkData.contract_id}
              >
                <SelectTrigger className="bg-[#1A1A1A] border-white/10" data-testid="bulk-job-type-select">
                  <SelectValue placeholder={bulkData.contract_id ? "Select job type" : "Pick contract first"} />
                </SelectTrigger>
                <SelectContent className="bg-[#2B2B2B] border-white/10">
                  {jobTypes
                    .filter(jt => jt.contract_id === bulkData.contract_id)
                    .map(jt => (
                      <SelectItem key={jt.id} value={jt.id}>{jt.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Preview */}
          {bulkPreview && (
            <div className="p-4 bg-[#1A1A1A] rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[#F2F2F2] font-medium">Preview: {bulkPreview.total_shifts} shifts</span>
                <div className="text-sm text-[#A1A1AA]">
                  Est. Cost: <span className="text-[#1ABC9C]">{fmt(bulkPreview.estimated_total_payable, orgCurrency, { decimals: 0 })}</span>
                </div>
              </div>
              <ScrollArea className="h-32">
                <div className="space-y-1">
                  {bulkPreview.shifts?.map((shift, idx) => (
                    <div key={idx} className="text-xs text-[#A1A1AA] flex justify-between">
                      <span>{shift.day_of_week}, {shift.date}</span>
                      <span>{shift.start_time} - {shift.end_time}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-white/5 pt-4">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="text-[#A1A1AA]"
          >
            Cancel
          </Button>
          {!bulkPreview ? (
            <Button
              onClick={onPreview}
              disabled={bulkLoading}
              className="bg-[#FFC04D]/20 text-[#FFC04D] hover:bg-[#FFC04D]/30"
            >
              {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Preview Shifts
            </Button>
          ) : (
            <Button
              onClick={onCreate}
              disabled={bulkLoading}
              className="bg-[#1ABC9C] hover:bg-[#1ABC9C]/90"
            >
              {bulkLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create {bulkPreview.total_shifts} Shifts
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BulkCreateModal;
